import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { delay, map, Observable, of, tap } from 'rxjs';
import {
  CreditPointsApi,
  CreditPointsData,
  CreditPointsValue,
  DepartmentType,
  EarningReason,
  JAPAN_STATES_MOCK,
  LoyaltyProfile,
  MiddlewareBaseResponse,
  PaginatedDto,
  PointCalculationResult,
  PointsMetadata,
  PointType,
  PredefinedValue,
  ProfileLoyaltyApi,
  RedeemPointsApi,
  RedeemPointsData,
  RedeemPointsValue,
  RevenueCalculationData,
  TriggeredEvent,
} from '@ireckonu/shared-ui';

import { APP_ENV } from '../app-config';
import { LoyaltyService } from './loyalty.service';
import {
  CREDIT_POINTS_DATA_MOCK,
  REDEEM_POINTS_DATA_MOCK,
} from './profile-loyalty.mocks';

interface PaginatedResponse<T> {
  Items: T[];
  TotalItems: number;
  Page?: number;
  PageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class LoyaltyDialogsService
  implements CreditPointsApi, RedeemPointsApi, ProfileLoyaltyApi
{
  private readonly http = inject(HttpClient);
  private readonly loyaltyService = inject(LoyaltyService);
  private readonly configApiBaseUrl = inject(APP_ENV).configApiBaseUrl;

  private get baseUrl(): string {
    return this.configApiBaseUrl + '/proxy/loyalty/api/v1';
  }

  setRedeemPoints(
    loyaltyProfileId: number,
    enrollmentId: number,
    formValue: RedeemPointsValue,
  ): Observable<LoyaltyProfile> {
    return this.http
      .post<MiddlewareBaseResponse<LoyaltyProfile>>(
        `${this.baseUrl}/loyalty-profiles/${loyaltyProfileId}/enrollments/${enrollmentId}/point-redemptions`,
        {
          loyaltyProfileId,
          enrollmentId,
          ...formValue,
        },
      )
      .pipe(
        map((response) => response.Data),
        tap((profile) =>
          this.loyaltyService.invalidateLoyaltyProfile(profile?.ExternalId),
        ),
      );
  }

  calculateRevenueByPoints(
    triggeredEventId: number,
    department: DepartmentType,
    points: number,
  ): Observable<RevenueCalculationData> {
    return this.http
      .get<MiddlewareBaseResponse<RevenueCalculationData>>(
        `${this.baseUrl}/triggered-events/${triggeredEventId}/calculations/revenue`,
        {
          params: new HttpParams({
            fromObject: {
              department,
              points: points.toString(),
            },
          }),
        },
      )
      .pipe(map((response) => response.Data));
  }

  getRedeemPointsData(): Observable<RedeemPointsData> {
    // Note: Replace mock with actual API call when ready
    // return this.http.get<MiddlewareBaseResponse<RedeemPointsData>>(
    //   `${this.baseUrl}/loyalty/redeem-points-data`,
    // ).pipe(map((response) => response.Data));
    return of(REDEEM_POINTS_DATA_MOCK).pipe(
      delay(1000),
      map((response) => response.Data),
    );
  }

  getTriggeredEvents(): Observable<TriggeredEvent[]> {
    return this.http
      .get<
        MiddlewareBaseResponse<PaginatedResponse<TriggeredEvent>>
      >(`${this.baseUrl}/triggered-events`)
      .pipe(map((response) => response.Data?.Items ?? []));
  }

  getPointTypes(configurationId: number): Observable<PointType[]> {
    return this.http
      .get<
        MiddlewareBaseResponse<PaginatedResponse<PointType>>
      >(`${this.baseUrl}/configurations/${configurationId}/point-types`)
      .pipe(map((response) => response.Data?.Items ?? []));
  }

  getCreditPointsData(): Observable<CreditPointsData> {
    // Note: Replace mock with actual API call when ready
    // return this.http.get<MiddlewareBaseResponse<CreditPointsData>>(
    //   `${this.baseUrl}/loyalty/credit-points-data`,
    // ).pipe(map((response) => response.Data));
    return of(CREDIT_POINTS_DATA_MOCK).pipe(
      delay(1000),
      map((response) => response.Data),
    );
  }

  getEarningReasons(): Observable<EarningReason[]> {
    return this.http
      .get<
        MiddlewareBaseResponse<PaginatedResponse<EarningReason>>
      >(`${this.baseUrl}/earning-reasons`)
      .pipe(map((response) => response.Data?.Items ?? []));
  }

  calculatePointsByRevenue(
    triggeredEventId: number,
    loyaltyProfileId: number,
    metadata: PointsMetadata,
  ): Observable<PointCalculationResult[]> {
    return this.http
      .get<MiddlewareBaseResponse<PointCalculationResult[]>>(
        `${this.baseUrl}/triggered-events/${triggeredEventId}/calculations/points`,
        {
          params: new HttpParams({
            fromObject: {
              loyaltyProfileId: loyaltyProfileId.toString(),
              ...(metadata as unknown as Record<string, string>),
            },
          }),
        },
      )
      .pipe(map((response) => response.Data ?? []));
  }

  setCreditPoints(
    loyaltyProfileId: number,
    enrollmentId: number,
    payload: CreditPointsValue,
  ): Observable<LoyaltyProfile> {
    return this.http
      .post<MiddlewareBaseResponse<LoyaltyProfile>>(
        `${this.baseUrl}/loyalty-profiles/${loyaltyProfileId}/enrollments/${enrollmentId}/point-credits`,
        {
          loyaltyProfileId,
          enrollmentId,
          ...payload,
        },
      )
      .pipe(
        map((response) => response.Data),
        tap((profile) =>
          this.loyaltyService.invalidateLoyaltyProfile(profile?.ExternalId),
        ),
      );
  }

  loadJapanStates(): PredefinedValue[] {
    return JAPAN_STATES_MOCK;
  }

  createLoyaltyProfile(externalId: string): Observable<number> {
    return this.http
      .post<
        MiddlewareBaseResponse<LoyaltyProfile>
      >(`${this.baseUrl}/loyalty-profiles`, { ExternalId: `${externalId}` })
      .pipe(map((response) => response.Data.Id));
  }

  getLoyaltyProfile(profileId: string): Observable<LoyaltyProfile> {
    return this.http
      .get<MiddlewareBaseResponse<PaginatedDto<LoyaltyProfile>>>(
        `${this.baseUrl}/loyalty-profiles`,
        {
          params: {
            ExternalId: profileId,
          },
        },
      )
      .pipe(map((response) => response.Data?.Items[0]));
  }
}
